"use client";

import { Bot, Calendar } from "lucide-react";
import { useStore } from "@/lib/store";
import type { Issue } from "@/lib/types";
import { StatusIcon } from "@/components/ui/StatusIcon";
import { PriorityIcon } from "@/components/ui/PriorityIcon";
import { Avatar } from "@/components/ui/Avatar";
import { BotBadge } from "@/components/ui/BotBadge";
import { AwaitingMeBadge } from "@/components/issues/AwaitingMeBadge";
import { formatRelative, formatDate, cn } from "@/lib/utils";

export function IssueRow({ issue, active }: { issue: Issue; active?: boolean }) {
  const users = useStore((s) => s.users);
  const labels = useStore((s) => s.labels);
  const projects = useStore((s) => s.projects);
  const selectIssue = useStore((s) => s.selectIssue);
  const compatibilityAssignee = users.find((u) => u.id === issue.assigneeId);
  const owner = users.find(
    (u) =>
      u.id ===
      (issue.ownerId ??
        (compatibilityAssignee && !compatibilityAssignee.isBot
          ? compatibilityAssignee.id
          : null))
  );
  const executor = users.find(
    (u) =>
      u.id ===
      (issue.executorAgentId ??
        (compatibilityAssignee?.isBot ? compatibilityAssignee.id : null))
  );
  const issueLabels = labels.filter((l) => issue.labelIds.includes(l.id));
  const project = projects.find((p) => p.id === issue.projectId);

  return (
    <button
      onClick={() => selectIssue(issue.id)}
      className={cn(
        "group w-full flex items-center gap-3 px-5 py-2 border-b border-[var(--border)] hover:bg-[var(--bg-hover)] transition-colors text-left",
        active && "bg-[var(--bg-hover)]"
      )}
    >
      <PriorityIcon priority={issue.priority} size={14} />
      <span className="text-[11.5px] font-mono text-[var(--fg-muted)] w-[64px] shrink-0">
        {issue.identifier}
      </span>
      <StatusIcon status={issue.status} size={14} />
      <span className="flex-1 min-w-0 truncate text-[13px] text-[var(--fg)] font-medium">
        {issue.title}
      </span>
      <BotBadge authorId={issue.authorId} className="shrink-0" />
      <AwaitingMeBadge issue={issue} />
      {project && (
        <span
          className="hidden md:flex items-center gap-1 px-1.5 py-0.5 rounded-[4px] text-[11px] text-[var(--fg-secondary)] border border-[var(--border)]"
          title={project.name}
        >
          <span>{project.icon}</span>
          <span className="truncate max-w-[100px]">{project.name}</span>
        </span>
      )}
      <div className="hidden lg:flex items-center gap-1 shrink-0">
        {issueLabels.slice(0, 3).map((l) => (
          <span
            key={l.id}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10.5px] border border-[var(--border)] text-[var(--fg-secondary)]"
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: l.color }} />
            {l.name}
          </span>
        ))}
      </div>
      {issue.dueDate && (
        <span className="hidden md:flex items-center gap-1 text-[11px] text-[var(--fg-tertiary)]">
          <Calendar size={11} />
          {formatDate(issue.dueDate)}
        </span>
      )}
      <span className="hidden sm:inline text-[11px] text-[var(--fg-muted)] w-[42px] text-right shrink-0">
        {formatRelative(issue.updatedAt)}
      </span>
      {executor && (
        <span
          className="hidden items-center gap-1 rounded border border-[var(--border)] bg-[var(--accent-bg)] px-1.5 py-0.5 text-[10.5px] text-[var(--brand-indigo)] md:inline-flex"
          title={`Agent: ${executor.name}`}
        >
          <Bot size={10} />
          <span className="max-w-[72px] truncate">{executor.name}</span>
        </span>
      )}
      <Avatar user={owner} size={20} />
    </button>
  );
}
